import { CreateUserInvitationRequestSchema } from '@easy-genomics/shared-lib/src/app/schema/easy-genomics/user-invitation';
import { OrganizationUserDetails } from '@easy-genomics/shared-lib/src/app/types/easy-genomics/organization-user-details';
import { OrganizationAccess } from '@easy-genomics/shared-lib/src/app/types/easy-genomics/user';
import { CreateUserInvitationRequest } from '@easy-genomics/shared-lib/src/app/types/easy-genomics/user-invitation';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { httpClient } from '@/lib/api/client';
import { VALIDATION_MESSAGES } from '@/lib/constants/validation';
import { decodeJwt } from '@/lib/utils/jwt-utils';
import { useUserStore } from '@/stores/user';

export type NameOptions = {
  firstName: string | null;
  preferredName: string | null;
  lastName: string | null;
  email: string | null;
};

export default function useUser() {
  function displayName(nameOptions: NameOptions): string {
    const { firstName, preferredName, lastName, email } = nameOptions;
    const preferredOrFirstName = preferredName || firstName;

    if (preferredOrFirstName && lastName) return `${preferredOrFirstName} ${lastName}`;
    if (preferredOrFirstName) return preferredOrFirstName;
    if (lastName) return lastName;
    if (email) return email;
    return '???';
  }

  function initials(nameOptions: NameOptions, superuser: boolean = false): string {
    if (superuser) {
      return '#';
    }

    const { preferredName, firstName, lastName, email } = nameOptions;
    const preferredOrFirstName = preferredName || firstName;

    const firstInitial = preferredOrFirstName?.charAt(0);
    const lastInitial = lastName?.charAt(0);
    const emailInitial = email?.charAt(0);

    if (firstInitial && lastInitial) return firstInitial + lastInitial;
    if (firstInitial) return firstInitial;
    if (lastInitial) return lastInitial;
    if (emailInitial) return emailInitial;
    return '??';
  }

  async function handleInvite(reqBody: CreateUserInvitationRequest, action: 'resend' | 'send') {
    const result = CreateUserInvitationRequestSchema.safeParse(reqBody);
    if (!result.success) {
      console.error('Zod validation failed', result.error);
      return;
    }

    const { Email: email } = result.data;
    const toastSuccessMessage =
      action === 'send' ? `${email} has been sent an invite` : `${email} has been resent an invite`;
    const toastErrorMessage = VALIDATION_MESSAGES.network;

    try {
      await httpClient.call('POST', '/user/create-user-invitation-request', result.data);
      toast.success(toastSuccessMessage);
    } catch (error) {
      toast.error(toastErrorMessage);
      console.error(error);
    }
  }

  async function resendInvite(user: { OrganizationId: string; UserEmail: string }) {
    const { OrganizationId: orgId, UserEmail: email } = user;
    await handleInvite(
      {
        OrganizationId: orgId,
        Email: email,
      },
      'resend',
    );
  }

  async function invite(reqBody: CreateUserInvitationRequest) {
    await handleInvite(reqBody, 'send');
  }

  function labsCount(user: OrganizationUserDetails) {
    const labsAccess = Object.values(user?.OrganizationAccess || {}).flatMap((orgAccess) =>
      Object.values(orgAccess?.LaboratoryAccess || {}),
    );
    return labsAccess.filter((labAccess) => labAccess.Status === 'Active').length;
  }

  async function setCurrentUserDataFromToken() {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('No auth token available');

      const decodedToken: any = decodeJwt(token);

      const isSuperuser = decodedToken['cognito:groups']?.includes('SystemAdmin');

      if (isSuperuser) {
        useUserStore.setState({
          currentUserDetails: {
            id: decodedToken['cognito:username'],
            email: decodedToken.email,
            firstName: null,
            lastName: null,
            preferredName: null,
          },
          currentUserPermissions: { isSuperuser: true, orgPermissions: null },
        });
        return;
      }

      const parsedOrgAccess = JSON.parse(decodedToken.OrganizationAccess);
      const currentLabId = useUserStore.getState().mostRecentLab.LaboratoryId;

      useUserStore.setState({
        currentUserDetails: {
          id: decodedToken['cognito:username'],
          email: decodedToken.email,
          firstName: decodedToken.FirstName ?? null,
          lastName: decodedToken.LastName ?? null,
          preferredName: decodedToken.PreferredName ?? null,
        },
        currentUserPermissions: {
          isSuperuser: false,
          orgPermissions: parsedOrgAccess as OrganizationAccess,
        },
        currentOrg: {
          OrganizationId: decodedToken.DefaultOrganization || Object.keys(parsedOrgAccess)[0],
        },
        mostRecentLab: {
          LaboratoryId: currentLabId || decodedToken.DefaultLaboratory || null,
        },
      });
    } catch (error) {
      console.error('Error occurred setting the current organization.', error);
      throw error;
    }
  }

  async function updateDefaultLab(labId: string): Promise<void> {
    useUserStore.setState({ mostRecentLab: { LaboratoryId: labId } });

    const { currentUserDetails, currentOrg } = useUserStore.getState();
    if (!currentUserDetails.id) return;

    await api.users.updateUserLastAccessInfo(currentUserDetails.id, currentOrg.OrganizationId ?? undefined, labId);
  }

  return {
    displayName,
    initials,
    labsCount,
    invite,
    resendInvite,
    setCurrentUserDataFromToken,
    updateDefaultLab,
  };
}
